import { JSX } from 'preact'
import { createPortal, memo } from 'preact/compat'
import { useEffect, useRef } from 'preact/hooks'
import { capitalize } from '@gsc/utils'
import { strWidgetId, inlineWidgetSelector } from '../../shared/widget'
import { WidgetInstance } from '../types'
import { useRenderComments } from '../hooks'
import { previewProps } from '../preview'
import { WidgetProvider } from './WidgetProvider'
import { Host } from './Host'
import { Shadow } from './Shadow'
import { WidgetRoot } from './WidgetRoot'
import { Teaser } from './Teaser'
import { useCssTransition } from './Transition'
import { StyleRegistryProvider, Styles, StylesV2 } from './Style'
import { BlockTreeProvider } from './Blocks'

const appName = RUNTIME_KEY ? capitalize(RUNTIME_KEY) : ''

const comments = {
  before: ['googleoff: all', ...(appName ? [`Created with ${appName}.com`] : [])],
  after: ['googleon: all'],
}

const useBodyOpenClassName = (widgetId: number, isOpen: boolean) => {
  useEffect(() => {
    const className = `${strWidgetId(widgetId)}-open`
    document.body.classList.toggle(className, isOpen)
    return () => document.body.classList.remove(className)
  }, [widgetId, isOpen])
}

const useWidgetContainer = () => {
  const elemRef = useRef<Element>(null)

  const getContainer = ({ widgetId, isMounted }: { widgetId: number; isMounted: boolean }) => {
    if (!isMounted) {
      elemRef.current = null
    } else if (!elemRef.current) {
      const selector = inlineWidgetSelector(widgetId)
      elemRef.current = selector ? document.querySelector(selector) : null
    }

    return {
      container: elemRef.current || document.body,
      inline: !!elemRef.current,
    }
  }

  return { getContainer }
}

export interface WidgetProps {
  widget: WidgetInstance
}

export const Widget = memo(function Widget({ widget }: WidgetProps): JSX.Element {
  const { getContainer } = useWidgetContainer()
  const { ref: commentsRef } = useRenderComments(comments)

  const { isMounted, ...props } = useCssTransition({ in: widget.show === 'main', appear: true })
  const { isMounted: isTeaserMounted, ...teaserProps } = useCssTransition({
    in: widget.show === 'teaser',
    appear: true,
  })

  useBodyOpenClassName(widget.id, isMounted)

  const { container, inline } = getContainer({ widgetId: widget.id, isMounted })
  const isV2 = widget.version === 'V2'

  return isMounted || isTeaserMounted
    ? createPortal(
        <Host ref={commentsRef} id={strWidgetId(widget.id)}>
          <WidgetProvider widgetId={widget.id}>
            <Shadow {...previewProps('root')}>
              <BlockTreeProvider>
                <StyleRegistryProvider>
                  {isV2 ? <StylesV2 widget={widget} /> : <Styles widget={widget} inline={inline} />}
                  {isMounted && <WidgetRoot widget={widget} {...props} />}
                  {isTeaserMounted && <Teaser widget={widget} {...teaserProps} />}
                </StyleRegistryProvider>
              </BlockTreeProvider>
            </Shadow>
          </WidgetProvider>
        </Host>,
        container,
      )
    : null
})
